import { useEffect, useRef } from 'react';
import { centroidFocusTarget, type FocusTarget } from '../components/Map/focus';
import type { Vendor } from '../types';

/**
 * Once-per-session deep-link pan: when the page loads with `/listing/…`
 * in the URL, fitBounds to all booths for that uid as soon as vendor
 * data arrives.
 *
 * - Captures the initial `vendorUid` at mount so this fires only for the
 *   URL-param selection that was present on first render, not for any
 *   subsequent user-driven selection changes.
 * - Guards against re-firing on data revalidation and on React
 *   StrictMode's double-invoked effects.
 * - Uses `requestAnimationFrame` so the map has had a chance to lay out
 *   before we issue the pan.
 *
 * `onFocus` should be a stable callback (e.g. a `useState` setter or a
 * `useCallback` with stable deps). The effect intentionally omits it
 * from its deps — the `firedRef` guard makes a re-run after identity
 * change irrelevant, but if `onFocus` were replaced before the first
 * fire, the closure would call the stale version.
 */
export function useDeepLinkFocus(
  vendorUid: string | null,
  vendors: Vendor[],
  onFocus: (target: FocusTarget) => void,
): void {
  const deepLinkedVendorUidRef = useRef(vendorUid);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!deepLinkedVendorUidRef.current || firedRef.current || vendors.length === 0) return;
    const filtered = vendors.filter((v) => v.uid === deepLinkedVendorUidRef.current);
    if (filtered.length === 0) return;
    firedRef.current = true;
    const booths = filtered as [Vendor, ...Vendor[]];
    const raf = requestAnimationFrame(() => onFocus(centroidFocusTarget(booths)));
    return () => cancelAnimationFrame(raf);
    // The `firedRef` guard ensures we only ever fire once, so re-running
    // when `onFocus` changes is safe and unnecessary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendors]);
}
